/**
 * Grouping + dependency-resolution for the permissions picker (views/users/index.tsx).
 *
 * `availablePermissions` comes from GET /user/permissions (the `Permission`
 * collection, seeded by migration 022-seedPermissionCatalog) — each entry
 * carries a `role` (module key, used to group the picker) and a `required`
 * array (other permission names that must come with it). This file resolves
 * that dependency graph client-side so checking one box can auto-check its
 * prerequisites, and unchecking a prerequisite cascades to whatever depended
 * on it — the `required` field used to be checked only when *adding* a
 * permission and silently ignored when removing one.
 */

export interface CatalogPermission {
    _id?: string;
    name: string;
    label: string;
    role: string;
    required: string[];
}

/** Display order + French label for each module (Permission.role value). */
export const MODULE_ORDER: { key: string; label: string }[] = [
    { key: 'dashboard', label: 'Tableau de bord' },
    { key: 'orders', label: 'Commandes' },
    { key: 'customers', label: 'Clients' },
    { key: 'suppliers', label: 'Fournisseurs & Achats' },
    { key: 'stock', label: 'Produits & Stock' },
    { key: 'production', label: 'Production' },
    { key: 'delivery', label: 'Livraison' },
    { key: 'invoicing', label: 'Facturation' },
    { key: 'pricing', label: 'Tarifs' },
    { key: 'kpi', label: 'KPI' },
    { key: 'okr', label: 'Pilotage OKR' },
    { key: 'users', label: 'Utilisateurs' },
    { key: 'other', label: 'Autres' },
];

/**
 * Universal baseline — always granted, never shown as a toggle: `dashboard`
 * (backend data access) and `menu:dashboard` (sidebar link visibility,
 * routes.tsx) are two independent strings for historical reasons, both
 * force-included on every save (mirrored server-side by
 * withBaselinePermission in userController.ts).
 */
export const BASELINE_PERMISSIONS = ['dashboard', 'menu:dashboard'];
const isBaseline = (name: string) => BASELINE_PERMISSIONS.includes(name);

/** Every permission `name` currently checked, plus everything it (transitively) requires. */
export function resolveWithDependencies(selected: string[], catalog: CatalogPermission[]): string[] {
    const byName = new Map(catalog.map((p) => [p.name, p]));
    const result = new Set<string>(BASELINE_PERMISSIONS);

    const visit = (name: string) => {
        if (result.has(name)) return;
        result.add(name);
        const entry = byName.get(name);
        entry?.required?.forEach(visit);
    };
    selected.forEach(visit);

    return Array.from(result);
}

/**
 * Removing `name` from `current`: also drop anything left in the set whose
 * `required` chain depended on it, repeating until stable (so removing a
 * permission two levels deep in the dependency chain cascades correctly).
 * Never drops the baseline.
 */
export function cascadeRemoval(current: string[], name: string, catalog: CatalogPermission[]): string[] {
    if (isBaseline(name)) return current;
    let remaining = current.filter((p) => p !== name);

    let changed = true;
    while (changed) {
        changed = false;
        const stillPresent = new Set(remaining);
        remaining = remaining.filter((p) => {
            if (isBaseline(p)) return true;
            const entry = catalog.find((c) => c.name === p);
            if (!entry) return true;
            const missingRequirement = entry.required.some((r) => !isBaseline(r) && !stillPresent.has(r));
            if (missingRequirement) changed = true;
            return !missingRequirement;
        });
    }
    return remaining;
}

export interface Poste {
    key: string;
    label: string;
    description: string;
    /** Seed permissions — expanded through resolveWithDependencies before being applied. */
    basePermissions: string[];
}

/**
 * A poste's `basePermissions` before dependency resolution. Direction is
 * '`all`' functionally, but for the checkbox list to visibly show every box
 * ticked (clearer for a non-technical owner than an opaque 'all' flag), it
 * expands to every known permission name plus 'all'.
 */
export function posteSeedPermissions(poste: Poste, catalog: CatalogPermission[]): string[] {
    if (poste.basePermissions.includes('all')) {
        return [...catalog.map((p) => p.name), 'all'];
    }
    return poste.basePermissions;
}

export const POSTES: Poste[] = [
    {
        key: 'commercial',
        label: 'Commercial',
        description: 'Clients, commandes, tarifs',
        // read:deliveryTour: the customer/order forms this poste lives in
        // all show a "Tournée de livraison" dropdown — needs to see tours,
        // never to create/edit/delete them.
        basePermissions: ['menu:orders', 'menu:customers', 'pricing', 'read:deliveryTour'],
    },
    {
        key: 'production',
        label: 'Production',
        description: 'Produits, stock, production, préparations',
        // read:orders + read-assigned:orders: the prepare-production planning
        // screen needs to see the pending order queue (what to produce) —
        // read:orders specifically so it sees every order, not just ones
        // whose customer happens to be assigned to this account (production
        // staff generally aren't assigned customers at all).
        // prepare:orders: mark a line/order prepared or ready, resolve an
        // issue, set lot numbers — the actual point of the prep screen —
        // without the full commercial order-edit/delete rights that
        // edit-assigned:orders also carries.
        basePermissions: ['menu:stock', 'menu:production', 'menu:preparations', 'read:deliveryTour', 'read-assigned:orders', 'read:orders', 'prepare:orders'],
    },
    {
        key: 'logistique',
        label: 'Logistique',
        description: 'Livraison, tournées, préparations, stock en lecture',
        // deliveryTour (not just read:): managing tours is the point of this poste.
        basePermissions: ['menu:delivery', 'deliveryTour', 'menu:preparations', 'read:stock'],
    },
    {
        key: 'achats',
        label: 'Achats',
        description: 'Fournisseurs, achats, stock en lecture',
        basePermissions: ['menu:suppliers', 'read:stock', 'read:deliveryTour'],
    },
    {
        key: 'comptabilite',
        label: 'Comptabilité',
        description: 'Facturation, tarifs',
        basePermissions: ['menu:invoicing', 'pricing'],
    },
    {
        key: 'direction',
        label: 'Direction',
        description: 'Accès complet à tous les modules',
        // 'all' bypasses every checkPermissions call — only the owner or
        // another 'all'-holder can apply it (see permissionsBeyondGranterLevel
        // in userController.ts, which the backend enforces independently).
        basePermissions: ['all'],
    },
];
